import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import segoeImage from '../../src/images/segoe.svg';
import sfImage from '../../src/images/sf.svg';
import sfMonoImage from '../../src/images/sfMono.svg';
import interImage from '../../src/images/interUI.svg';
import ubuntuImage from '../../src/images/ubuntu.svg';
import { Box } from '@primer/react';
export const meta = {
  displayName: 'Typography'
};
export const _frontmatter = {};
const layoutProps = {
  meta,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`These guidelines summarize how we use typography at GitHub, and serve as guardrails for designers to design freely with best typographic practices in mind.`}</p>
    <h2>{`Typefaces`}</h2>
    <p>{`The first three typefaces listed below are the most commonly utilized system-fonts on GitHub products. However, this isn’t a finite list, so keep in mind that typographic implementations may vary between browser settings and operating systems.`}</p>
    <p><a parentName="p" {...{
        "href": "https://rsms.me/inter/"
      }}>{`Inter UI`}</a>{` and `}<a parentName="p" {...{
        "href": "https://developer.apple.com/fonts/"
      }}>{`SF Mono`}</a>{` are not system fonts, but are used in specialized scenarios to differentiate information and establish hierarchy.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Example`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><img src={sfImage} alt="San Francisco" width="1000" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/fonts/"
            }}>{`San Francisco`}</a>{` is a neo-grotesque sans-serif typeface designed by Apple to replace Helvetica Neue for OSX and iOS`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><img src={segoeImage} alt="Segoe" width="1000" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.microsoft.com/en-us/fabric#/resources"
            }}>{`Segoe UI`}</a>{` is a humanist sans-serif adopted by Microsoft as its default operating system font since Windows Vista`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><img src={ubuntuImage} alt="Ubuntu" width="1000" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://design.ubuntu.com/font/"
            }}>{`Ubuntu`}</a>{` is an OpenType-based, humanist sans-serif typeface. It became the default system font for Ubuntu 10.10 and is commonly used on Linux distros`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><img src={interImage} alt="Inter" width="1000" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://rsms.me/inter/"
            }}>{`Inter UI`}</a>{` is a typeface designed by Rasmus Andersson specially for user interfaces. Inter UI is used in our products as a display face in select circumstances`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><img src={sfMonoImage} alt="SF Mono" width="1000" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developer.apple.com/fonts/"
            }}>{`SF Mono`}</a>{` is a monospace typeface that is part of the San Francisco typeface family. SF Mono is monospaced across weights, so resetting SF Mono text into another font weight will not cause text to reflow`}</td>
        </tr>
      </tbody>
    </table>
    <h2>{`Font stack`}</h2>
    <p>{`We utilize system fonts at GitHub, which allow for optimized performance. This design decision takes advantage of retina screens, dynamic kerning, additional font-weights, and improved readability.`}</p>
    <p><strong parentName="p">{`System Fonts`}</strong>{`—default GitHub font-stack with ample fallbacks alongside emoji support`}</p>
    <pre><code parentName="pre" {...{}}>{`font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
`}</code></pre>
    <p><strong parentName="p">{`Monospace`}</strong>{`—used within GitHub product to display metadata or captions, and used in marketing contexts as subheaders`}</p>
    <pre><code parentName="pre" {...{}}>{`$mono-font: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace !default;
`}</code></pre>
    <p><strong parentName="p">{`Display face`}</strong>{`—the font stack that implements Inter UI`}</p>
    <pre><code parentName="pre" {...{}}>{`font-family: InterUI, -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Oxygen, Ubuntu, Cantarell, "Open Sans", sans-serif;
`}</code></pre>
    <p><em parentName="p">{`For more information on why we made the change to system fonts, check out `}<a parentName="em" {...{
          "href": "http://markdotto.com/"
        }}>{`Mark Otto`}</a>{`'s article: `}<a parentName="em" {...{
          "href": "http://markdotto.com/2018/02/07/github-system-fonts/"
        }}>{`Shipping System fonts to GitHub.com`}</a></em></p>
    <h2>{`Typography scale and line height`}</h2>
    <p>{`The typography scale is designed to work for GitHub's product interfaces and marketing sites. Font sizes are designed to work in combination with line-height values so as to result in more sensible numbers wherever possible.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Font size: desktop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`1.25 line-height`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`1.5 line-height`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`12px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`15`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`18`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`14px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`17.5`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`21`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`16px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`20`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`24`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`20px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`25`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`24px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`36`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`32px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`40`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`48`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`40px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`50`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`60`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`48px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`60`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`72`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`Marketing typography scale`}</h3>
    <p>{`Our marketing typography scale aligns with our core typography scale, except for one additional header size:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Font size: desktop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`1.25 line height`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`1.5 line height`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`64px`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`80`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`96`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Marketing typography also incorporates `}<a parentName="p" {...{
        "href": "https://rsms.me/inter/"
      }}>{`Inter UI`}</a>{` as a display face.
`}<em parentName="p">{`More information on `}<a parentName="em" {...{
          "href": "https://primer.style/css/utilities/marketing-type"
        }}>{`marketing typography`}</a></em>{`.`}</p>
    <h2>{`Applying typography`}</h2>
    <p>{`It is best practice at GitHub to keep unique type styling to a minimum as to align stylistically with our other products.`}</p>
    <h3>{`Hierarchy`}</h3>
    <p>{`When establishing hierarchy for GitHub products, designers should remember that we stress efficient, clean reading experiences for our users. Be sure to utilize Primer’s `}<a parentName="p" {...{
        "href": "https://primer.style/css/utilities/typography"
      }}>{`typography scale`}</a>{` and `}<a parentName="p" {...{
        "href": "https://primer.style/css/utilities/typography"
      }}>{`typography utilities`}</a>{` when doing so. Refrain from utilizing color as a primary method of emphasis.`}</p>
    <h3>{`Readability`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Line height`}</strong>{`—Refer to our typography scale when structuring line height. Generally, our products should follow this scale without issues.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Line length`}</strong>{`—It's considered a good practice to keep lines around 80 characters or less—`}<a parentName="p" {...{
            "href": "https://www.w3.org/WAI/tutorials/page-structure/styling/"
          }}>{`W3 Page Structure Guidelines`}</a>{`. Cases vary depending on the situation, but this serves as a general rule of thumb for designing layouts.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Type alignment`}</strong>{`—Our content is typically left-aligned and ragged right. Be sensible in deviating from this standard, as justifying, centering, and right-aligning text is atypical of GitHub products.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Font weight`}</strong>{`—Adjust font weight to add emphasis and differentiate content hierarchy.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Letter spacing`}</strong>{`—Please refrain from altering letter-spacing on our products. There may be rare scenarios in which this is acceptable, but generally speaking, this should be avoided altogether.`}</p>
      </li>
    </ul>
    <h2>{`Support`}</h2>
    <h3>{`Iterate and componentize`}</h3>
    <p>{`When certain typographic patterns in your design emerge, or you believe a certain solution should be made into a component, reach out to the `}<a parentName="p" {...{
        "href": "https://github.slack.com/archives/CSGAVNZ19"
      }}>{`#primer`}</a>{` channel on Slack to find out more on submitting your code or idea. You may also reach out by opening an issue in `}<a parentName="p" {...{
        "href": "https://github.com/primer/primer"
      }}>{`the Primer repo on GitHub`}</a>{`.`}</p>
    <h3>{`Related typography components`}</h3>
    <p>{`Primer includes many typographic solutions. Please utilize them when possible before creating a custom component. Some include:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/alerts"
        }}>{`Alerts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/labels"
        }}>{`Labels`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/subhead"
        }}>{`Subheads`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://primer.style/css/components/navigation"
        }}>{`Navigation`}</a></li>
    </ul>
    <h3>{`Further reading and resources`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://jxnblk.com/blog/mathematical-web-typography/"
        }}>{`Mathematical Web Typography`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.amazon.com/Elements-Typographic-Style-Robert-Bringhurst/dp/0881791326"
        }}>{`The Elements of Typographic Style`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.amazon.com/Type-Primer-2nd-John-Kane-ebook/dp/B006Y1X55G"
        }}>{`A Type Primer`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://thinkingwithtype.com/"
        }}>{`Thinking with Type`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.w3.org/WAI/tutorials/page-structure/"
        }}>{`WCAG Accessibility Tutorials`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://type-scale.com/"
        }}>{`Browser Typography Scale`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.typewolf.com"
        }}>{`Typewolf Blog`}</a></li>
    </ul>
    {
      /* **etc.** */
    }


    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      